/**
 * Admin Role Management Service
 * API: /api/admin/roles
 */

import client from '@lib/core/axios.client';
import { API_ENDPOINTS } from '@lib/constants';
import type {
  RoleDto,
  RoleUserDto,
  CreateRoleCommand,
  UpdateRoleCommand,
} from '@/types/api/admin';

export class AdminRoleService {
  private static instance: AdminRoleService;

  private constructor() {}

  public static getInstance(): AdminRoleService {
    if (!AdminRoleService.instance) {
      AdminRoleService.instance = new AdminRoleService();
    }
    return AdminRoleService.instance;
  }

  /**
   * Get all roles in the system
   */
  async getRoles(): Promise<RoleDto[]> {
    return await client.get<RoleDto[]>(API_ENDPOINTS.ADMIN.ROLES.LIST);
  }

  /**
   * Get role by ID
   */
  async getRoleById(roleId: string): Promise<RoleDto> {
    return await client.get<RoleDto>(API_ENDPOINTS.ADMIN.ROLES.BY_ID(roleId));
  }

  /**
   * Create new role
   */
  async createRole(data: CreateRoleCommand): Promise<RoleDto> {
    return await client.post<RoleDto>(API_ENDPOINTS.ADMIN.ROLES.CREATE, data);
  }

  /**
   * Update existing role
   */
  async updateRole(roleId: string, data: UpdateRoleCommand): Promise<RoleDto> {
    return await client.put<RoleDto>(
      API_ENDPOINTS.ADMIN.ROLES.UPDATE(roleId),
      data
    );
  }

  /**
   * Delete role (cannot delete static roles or roles with users)
   */
  async deleteRole(roleId: string): Promise<void> {
    return await client.delete<void>(API_ENDPOINTS.ADMIN.ROLES.DELETE(roleId));
  }

  /**
   * Get all users assigned to a specific role
   */
  async getRoleUsers(roleId: string): Promise<RoleUserDto[]> {
    return await client.get<RoleUserDto[]>(
      API_ENDPOINTS.ADMIN.ROLES.USERS(roleId)
    );
  }

  /**
   * Assign a role to a user
   */
  async assignRoleToUser(roleId: string, userId: string): Promise<void> {
    return await client.post<void>(
      API_ENDPOINTS.ADMIN.ROLES.ASSIGN_USER(roleId, userId)
    );
  }

  /**
   * Remove a role from a user
   */
  async removeRoleFromUser(roleId: string, userId: string): Promise<void> {
    return await client.delete<void>(
      API_ENDPOINTS.ADMIN.ROLES.REMOVE_USER(roleId, userId)
    );
  }
}

// Export singleton instance
export const adminRoleService = AdminRoleService.getInstance();
